import { reactive } from 'vue'
import type {
  CategoryWithResults,
  SearchScopeCategory,
} from '@/services/search-service/categories/types'

import type { SearchResultItem } from '@onxmaps/search-web'

const observableState: { currentCategoryGroup: string } = reactive({
  currentCategoryGroup: 'All',
})

// Set and retrieve current group
/**
 * *(AI-generated)*
 * Returns the current category group.
 */
export const currentCategoryGroup = () => observableState.currentCategoryGroup
/**
 * *(AI-generated)*
 * Sets the current category group.
 */
export const setCurrentCategoryGroup = (group: string) =>
  (observableState.currentCategoryGroup = group)

/**
 * Determine category groups from current search results and
 * create a list of category groups with search results
 */
export const separateSearchResultsByCategories = (
  results: SearchResultItem[]
) => {
  if (!results?.length) {
    return []
  }

  const categories: Array<CategoryWithResults> = []

  // create categories from results
  results.forEach((res) => {
    const searchScopes = res.item.searchScopes

    if (searchScopes?.length) {
      const cat = categories.find((cat) => cat.name === searchScopes[0].name)
      if (cat !== undefined) {
        cat.results.push(res)
      } else {
        categories.push({
          name: searchScopes[0].name as string,
          priority: searchScopes[0].priority as number,
          results: [res],
        })
      }
    }
  })

  // sort categories by priority
  categories.sort((cat1, cat2) => (cat1.priority > cat2.priority ? 1 : -1))

  return categories
}

/**
 * Get the zero query search categories for the current app
 */
export const getZeroQueryCategories = () => {
  const categories: SearchScopeCategory[] = [
    { name: 'Water', slug: 'water', icon: 'waterbody' },

    { name: 'Roads', slug: 'roads', icon: 'add-route' },
    {
      name: 'Trails',
      slug: 'trails',
      icon: 'waypoint-type/backcountry-trailhead',
    },
  ]

  return categories
}

/**
 * Gets sub-types (onxType) associated with a searchScope
 * @param searchScope - searchScope slug (Knowledge Graph)
 * @returns array of onxTypes along with nice label and priority
 */
const getZeroQueryCategoriesTypes = () => {
  const categoryTypes: Array<{
    name: string
    label: string
    priority: number
  }> = []

  return categoryTypes
}

/**
 * Determine category groups using onxType from current search results and
 * create a list of category groups with search results
 */
export const separateSearchResultsByOnxType = (results: SearchResultItem[]) => {
  if (!results.length) {
    return []
  }

  const categories: Array<CategoryWithResults> = []

  // create categories from results
  results.forEach((res) => {
    if (res.item.onxType) {
      const cat = categories.find((cat) => cat.name === res.item.onxType)
      if (cat !== undefined) {
        cat.results.push(res)
      } else {
        categories.push({
          name: res.item.onxType,
          priority: -1,
          results: [res],
        })
      }
    }
  })

  return categories
}

/**
 * Maps and prioritizes zero query sub categories (onxTypes)
 *
 * @param searchScope - searchScope slug (Knowledge Graph)
 * @param categories - array of categories with results
 * @returns mapped categories
 */
export const mapZeroQuerySubCategories = (
  categories: Array<CategoryWithResults>
) => {
  const searchScopeOnxTypes = getZeroQueryCategoriesTypes()
  const mappedCategories: Array<CategoryWithResults> = []

  categories.forEach((cat) => {
    const typeCat = searchScopeOnxTypes.find((type) => cat.name === type.name)
    if (typeCat) {
      mappedCategories.push({
        ...cat,
        name: typeCat.label,
        priority: typeCat.priority,
      })
    }
  })

  // sort categories by priority
  return mappedCategories.sort((cat1, cat2) => {
    return cat1.priority > cat2.priority ? 1 : -1
  })
}
